import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { LoadExploreGroupsAction } from './explore-groups.actions';
import { GroundFoundDto, GroupsService } from '@smatchy/sdk';
import { FilterEventsSelector } from '@smatchy/core';
import { buildLocationSearchFields } from '../../../utils/build-location-search-fields.util';
import { catchError, of, take, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ShowToastErrorAction } from '../../app';
import { TranslateService } from '@ngx-translate/core';

export interface ExploreGroupsStateModel {
  groups: GroundFoundDto[];
}

const defaults: ExploreGroupsStateModel = {
  groups: [],
};

@State<ExploreGroupsStateModel>({
  name: 'exploreGroups',
  defaults,
})
@Injectable()
export class ExploreGroupsState {
  constructor(
    private readonly _groupsService: GroupsService,
    private readonly _store: Store,
    private readonly _translate: TranslateService,
  ) {}

  @Action(LoadExploreGroupsAction)
  loadExploreGroupsAction({ patchState }: StateContext<ExploreGroupsStateModel>, { payload }: LoadExploreGroupsAction) {
    const filter = this._store.selectSnapshot(FilterEventsSelector.filter);
    const locationFields = buildLocationSearchFields(filter);
    const hasLocationFilter =
      locationFields.searchMode === 'hierarchy' ? !!locationFields.hierarchy?.level : !!locationFields.location?.lat;

    if (!hasLocationFilter) {
      patchState({ groups: [] });
      return;
    }

    return this._groupsService
      .searchGroups({
        findGroupsDto: {
          ...locationFields,
          notJoined: payload.notJoined,
          notFromMe: payload.notFromMe,
        },
      })
      .pipe(
        take(1),
        tap((res) => {
          if (res.error) {
            // Said "ERROR ! Implement it" in a system popup, on a person's
            // phone. What was missing was not a nicer popup: it was deciding
            // what the screen does when the search fails. It says so, and keeps
            // whatever was already listed rather than blanking it.
            this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GROUPS.ERRORS.SEARCH_FAILED')));
            return;
          }
          patchState({
            groups: res.value?.groups,
          });
        }),
        catchError((error: unknown) => {
          // Status 0 means the request never completed: offline, or the app went
          // to the background mid-request (CAPACITOR-18, on /home/explore). The
          // explore page awaits this action, so a throw here rejected that await
          // and reached Sentry as an unhandled error. Same treatment as
          // CAPACITOR-1 on my groups: a network drop stays silent, keeping what
          // was already listed, and any other failure says the search failed.
          const isNetworkDrop = error instanceof HttpErrorResponse && error.status === 0;
          if (!isNetworkDrop) {
            this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GROUPS.ERRORS.SEARCH_FAILED')));
          }
          return of(null);
        }),
      );
  }
}
